<template>
  <v-container fluid class="withdraw-page main-container responsive-height" :class="{ 'withdraw-page--dark': $vuetify.theme.dark }">
    <header class="withdraw-heading">
      <div>
        <span class="withdraw-heading__icon"><v-icon>mdi-wallet</v-icon></span>
        <div><h1>Withdraw Funds</h1><p>Choose your preferred withdrawal method to transfer funds securely.</p></div>
      </div>
      <span class="withdraw-secure-badge"><v-icon size="17">mdi-shield-check-outline</v-icon>Secure withdrawal</span>
    </header>
    <v-row>
      <v-col cols="12" md="6" sm="6" lg="4" v-for="method in methods" :key="method.id">
        <v-card class="elevation-0 transition-method-card withdraw-method-card">
          <v-card-actions class="paymentImage">
            <v-img height="155" contain :src="getMethodImage(method.method_name)" :alt="method.method_name">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="amber darken-1"></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-card-actions>
          <v-card-actions>
            <div class="value">
              <span>Method</span>
              <span>{{ method.method_name }}</span>
            </div>
            <v-spacer></v-spacer>
            <div class="value">
              <span>Currency</span>
              <span>{{ getCurrencyName(method.currency_id) }}</span>
            </div>
          </v-card-actions>
          <v-card-actions>
            <div class="value">
              <span>Processing Time</span>
              <span>{{ method.processing_time }}</span>
            </div>
            <v-spacer></v-spacer>

            <div class="value mr-6" v-if="method.method_name === 'Crypto'">
              <span>Fees</span>
              <span>0.5-10$</span>
            </div>

            <!-- Other Fees -->
            <div class="value mr-6" v-else>
              <span>Fees</span>
              <span>{{ method.commission }}%</span>
            </div>

            <!-- <div class="value mr-6">
              <span>Fees</span>
              <span>{{ method.commission }}%</span>
            </div> -->
          </v-card-actions>

          <v-card-actions class="pb-0 pt-3">
            <v-dialog transition="scale-transition" origin="center center" max-width="554"
              content-class="deposit-withdraw-popup">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined elevation="0" class="text-capitalize btn-account withdraw-details-button" v-bind="attrs"
                  v-on="on">Details</v-btn>
              </template>
              <template v-slot:default="dialog">
                <v-card class="withdraw-detail-card" :class="{ 'withdraw-detail-card--dark': $vuetify.theme.dark }">
                  <v-card-title class="rounded-0">{{ method.method_name }} Detail</v-card-title>
                  <v-card-text>
                    <p>Currency: <span>{{ getCurrencyName(method.currency_id) }}</span></p>
                    <p v-if="method.method_name === 'Crypto'">
                      Fees:
                      <span>
                        TRC20 (1 USDT) ; ERC20 (10 USDT); BEP20 (0.5 USDT)
                      </span>
                    </p>

                    <!-- 🔥 Otherwise show percentage commission -->
                    <p v-else>
                      Fees / Commission:
                      <span>{{ method.commission }}%</span>
                    </p>
                    <!-- <p>Fees / Commission: <span>{{ method.commission }}%</span></p> -->
                    <p>Processing Time: <span>{{ method.processing_time }}</span></p>
                    <p v-if="method.beneficiary_name">Beneficiary Name: <span>{{ method.beneficiary_name }}</span></p>
                    <p v-if="method.beneficiary_address">Beneficiary Address: <span>{{ method.beneficiary_address
                    }}</span></p>
                    <p v-if="method.beneficiary_bank_name">Bank Name: <span>{{ method.beneficiary_bank_name }}</span>
                    </p>
                    <p v-if="method.beneficiary_bank_swift">SWIFT: <span>{{ method.beneficiary_bank_swift }}</span></p>
                  </v-card-text>
                  <ol>
                    <li>Payments from third parties are not accepted. The name of the sender must match the name on your
                      trading account.</li>
                    <li>The minimum transaction amount is ${{ method.min_transaction }}</li>
                    <li>The maximum transaction amount is ${{ formatMaxAmount(method.max_transaction) }}</li>
                    <li>Processing time is typically {{ method.processing_time }}. There may be delays if additional
                      verification is required.</li>
                  </ol>
                </v-card>
              </template>
            </v-dialog>

            <v-btn :to="getUrl(method)" elevation="0" class="text-capitalize btn-account withdraw-select-button">Select</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="methods.length == 0" cols="12">
        <section class="withdraw-empty"><v-icon size="48">mdi-credit-card-off-outline</v-icon><h2>No withdrawal methods available</h2><p>Please check again later or contact support for assistance.</p></section>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { WITHDRAWMETHODS__ACTIONS } from "@/store/action-types";

export default {
  data: () => ({
    methodImages: {
      'Bank Transfer': require("@/assets/payments/bank.png"),
      'Cash Transfer': require("@/assets/payments/cash.png"),
      'Crypto': require("@/assets/payments/crypto.png")
    },
    currencyMap: {
      1: 'USD',
      2: 'EUR',
      3: 'USDT'
    }
  }),

  computed: {
    ...mapState("withdraw", ["methods"])
  },
  methods: {
    ...mapActions("withdraw", [
      WITHDRAWMETHODS__ACTIONS.WITHDRAWMETHODS
    ]),
    getMethodImage(methodName) {
      return this.methodImages[methodName] || '';
    },
    getUrl(method) {
      return method.method_name === 'Bank Transfer' ? `/bank-withdraw?payment_id=${method.id}` : method.method_name === 'Cash Transfer' ? `/cash-withdraw?payment_id=${method.id}` : `/crypto-withdraw?payment_id=${method.id}`
    },
    getCurrencyName(currencyId) {
      return this.currencyMap[currencyId] || 'USD';
    },
    formatMaxAmount(amount) {
      return Number(amount).toLocaleString();
    }
  },
  async mounted() {
    await this[WITHDRAWMETHODS__ACTIONS.WITHDRAWMETHODS]();
  }
};
</script>

<style lang="scss" scoped>
.withdraw-page {
  --withdraw-page: #f7f8fa; --withdraw-panel: #fff; --withdraw-soft: #f4f5f7; --withdraw-border: #dfe2e6;
  --withdraw-text: #171a1f; --withdraw-muted: #626973; --withdraw-gold: #d99a00;
  box-sizing: border-box; height: calc(100vh - 64px); max-width: none !important; padding: 16px 18px 24px !important;
  overflow-x: hidden; overflow-y: auto; color: var(--withdraw-text); background: var(--withdraw-page);
  overscroll-behavior-y: contain; -webkit-overflow-scrolling: touch;
}
.withdraw-page--dark {
  --withdraw-page: #030b13; --withdraw-panel: #07111b; --withdraw-soft: #0b1621; --withdraw-border: #293026;
  --withdraw-text: #f4f5f7; --withdraw-muted: #aeb6c0; --withdraw-gold: #ffc400;
  background: radial-gradient(circle at 68% 14%, rgba(255, 196, 0, .055), transparent 34%), var(--withdraw-page);
}
.withdraw-heading { min-height: 72px; display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 8px; padding: 0 4px; }
.withdraw-heading > div { display: flex; align-items: center; gap: 13px; }
.withdraw-heading__icon { width: 42px; height: 42px; display: inline-flex; align-items: center; justify-content: center; color: var(--withdraw-gold); border: 1px solid rgba(217, 154, 0, .42); border-radius: 9px; background: rgba(217, 154, 0, .09); }
.withdraw-heading__icon .v-icon, .withdraw-secure-badge .v-icon { color: inherit; }
.withdraw-heading h1 { margin: 0 0 3px; color: var(--withdraw-text); font-size: 23px; line-height: 1.25; }
.withdraw-heading p { margin: 0; color: var(--withdraw-muted); font-size: 13px; }
.withdraw-secure-badge { display: inline-flex; align-items: center; gap: 7px; padding: 7px 11px; color: var(--withdraw-gold); border: 1px solid rgba(217, 154, 0, .34); border-radius: 7px; background: rgba(217, 154, 0, .07); font-size: 12px; font-weight: 600; }
.withdraw-method-card { height: 100%; overflow: hidden; color: var(--withdraw-text) !important; border: 1px solid var(--withdraw-border); border-radius: 10px !important; background: var(--withdraw-panel) !important; transition: border-color .18s ease, transform .18s ease, box-shadow .18s ease; }
.withdraw-method-card:hover { border-color: var(--withdraw-gold); transform: translateY(-2px); box-shadow: 0 10px 26px rgba(43, 34, 5, .08) !important; }
.paymentImage { min-height: 174px; margin: 12px 12px 6px; padding: 12px !important; border: 1px solid var(--withdraw-border); border-radius: 8px; background: var(--withdraw-soft); }
.withdraw-method-card > .v-card__actions:not(.paymentImage) { min-height: 57px; padding: 10px 16px; border-top: 1px solid var(--withdraw-border); }
.value { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.value span:first-child { color: var(--withdraw-muted); font-size: 11px; }
.value span:last-child { color: var(--withdraw-text); font-size: 13px; font-weight: 600; }
.btn-account { min-width: 104px !important; height: 39px !important; border-radius: 6px !important; font-size: 12px; font-weight: 700; }
.withdraw-details-button { color: var(--withdraw-gold) !important; border-color: var(--withdraw-gold) !important; }
.withdraw-select-button { color: #17130a !important; background: linear-gradient(135deg, #ffd43b, #ffc400) !important; }
.withdraw-method-card > .v-card__actions:last-child { gap: 10px; justify-content: flex-end; padding-bottom: 14px !important; }
.withdraw-empty { min-height: 360px; display: grid; place-items: center; align-content: center; gap: 8px; color: var(--withdraw-muted); border: 1px solid var(--withdraw-border); border-radius: 10px; background: var(--withdraw-panel); text-align: center; }
.withdraw-empty .v-icon { color: var(--withdraw-gold); }
.withdraw-empty h2, .withdraw-empty p { margin: 0; }
.withdraw-empty h2 { color: var(--withdraw-text); font-size: 18px; }
.withdraw-empty p { font-size: 13px; }
@media (max-width: 760px) {
  .withdraw-page { height: calc(100vh - 56px); height: calc(100dvh - 56px); padding: 12px 10px 20px !important; }
  .withdraw-page > .row { margin-right: -6px; margin-left: -6px; }
  .withdraw-page > .row > [class*="col-"] { width: 100%; max-width: 100%; flex: 0 0 100%; padding: 6px; }
  .withdraw-heading { align-items: flex-start; }
  .withdraw-heading p, .withdraw-secure-badge { display: none; }
  .withdraw-heading h1 { font-size: 21px; }
  .withdraw-method-card > .v-card__actions:last-child {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 10px;
  }
  .withdraw-details-button,
  .withdraw-select-button {
    width: auto !important;
    min-width: 0 !important;
    max-width: none;
    margin: 0 !important;
    flex: 1 1 0;
  }
}
</style>

<style lang="scss">
.deposit-withdraw-popup .withdraw-detail-card { color: #171a1f !important; border: 1px solid #dfe2e6; border-radius: 10px !important; background: #fff !important; }
.deposit-withdraw-popup .withdraw-detail-card--dark { color: #f4f5f7 !important; border-color: #5a470c; background: #07111b !important; }
.deposit-withdraw-popup .withdraw-detail-card .v-card__title { color: inherit; border-bottom: 1px solid rgba(217, 154, 0, .28); font-size: 18px; font-weight: 700; }
.deposit-withdraw-popup .withdraw-detail-card .v-card__text { padding-top: 18px; color: inherit !important; }
.deposit-withdraw-popup .withdraw-detail-card p { display: flex; justify-content: space-between; gap: 20px; color: inherit; }
.deposit-withdraw-popup .withdraw-detail-card p span { color: #b47f00; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.deposit-withdraw-popup .withdraw-detail-card--dark p span { color: #ffc400; }
.deposit-withdraw-popup .withdraw-detail-card ol { margin: 0; padding: 0 34px 22px 46px; color: inherit; }
.deposit-withdraw-popup .withdraw-detail-card li { margin-bottom: 8px; line-height: 1.5; }
@media (max-width: 600px) {
  .deposit-withdraw-popup .withdraw-detail-card p { align-items: flex-start; flex-direction: column; gap: 4px; }
  .deposit-withdraw-popup .withdraw-detail-card p span { text-align: left; }
}
</style>
